/**
 * TTS 预生成音频 OSS 解析（tts3 edge 主通道写过的对象键）
 * - 语速不传 / -10：`sound/{Voice}/{en}.mp3`
 * - 其它语速：`sound/{Voice}/{speed.00}/{en}.mp3`（无音量后缀 `_0.50`）
 * 仅用于前端预取；预检未命中由 requestTts 回落 tts3；预检命中但播放失败由 ttsMixin 失效命中并 skipOss 重试 tts3（每词每会话一次）。
 *
 * 内存：预检复用单个 Audio + 结束后 pause/卸 src/load（对齐 ttsMixin._releaseTtsAudioElement）；
 * hit/miss 会话缓存有界 LRU，避免长课时 Map 单调增长。预检语义（候选顺序、超时、命中结果）不变。
 */
import {
  enToFilename,
  sentenceCase,
  titleCaseEachWord,
  titleCaseRestLower
} from '@/utils/wordImageResolve'

/** 与配音台 / 扫课上传一致（不含 tts-out/） */
export const TTS_SOUND_OSS_BASE = 'https://yingyuwangzhan.oss-cn-beijing.aliyuncs.com/sound'

const PROBE_TIMEOUT_MS = 2500
const MISS_TTL_MS = 5 * 60 * 1000
/** 命中 URL 缓存上限（仅存字符串） */
const HIT_CACHE_MAX = 100
/** miss 时间戳缓存上限；过期条目访问时删除 */
const MISS_CACHE_MAX = 100

/** @type {Map<string, string>} */
const hitCache = new Map()
/** @type {Map<string, number>} */
const missCache = new Map()
/** @type {Map<string, Promise<string>>} */
const inFlight = new Map()

/** 全局串行预检，安全复用同一 HTMLAudio（多词并发时只排队，不改变命中结果） */
let probeChain = Promise.resolve()
/** @type {HTMLAudioElement | null} */
let sharedProbeAudio = null

function encodeObjectKeyPath (objectKey) {
  return String(objectKey || '')
    .split('/')
    .map((seg) => encodeURIComponent(seg).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`))
    .join('/')
}

/** en-GB-SoniaNeural → Sonia；缺省 Sonia（不用 default） */
export function ttsVoiceFolderName (voiceType) {
  const s = String(voiceType || '').trim()
  if (!s || s === 'default') return 'Sonia'
  const neural = s.match(/-([A-Za-z]+)Neural$/i)
  if (neural) return neural[1]
  const tail = s.match(/-([A-Za-z]+)$/)
  if (tail) return tail[1]
  return 'Sonia'
}

/** 大小写候选文件名（OSS 区分大小写；与配图候选策略一致，后缀 .mp3） */
export function ttsMp3FilenameCandidates (en) {
  const base = enToFilename(en)
  if (!base) return []

  const baseNames = []
  const add = (name) => {
    const n = String(name || '').trim()
    if (n) baseNames.push(n)
  }

  const isLowerAlpha = base === base.toLowerCase() && /^[a-z]+$/.test(base)
  if (isLowerAlpha && base.length >= 3) {
    add(titleCaseRestLower(base))
    add(base)
  } else {
    add(base)
    add(titleCaseRestLower(base))
  }
  add(base.toLowerCase())
  add(base.toUpperCase())
  add(titleCaseEachWord(base))
  add(sentenceCase(base))

  const seen = new Set()
  const out = []
  for (const name of baseNames) {
    const filename = name.toLowerCase().endsWith('.mp3') ? name : `${name}.mp3`
    if (seen.has(filename)) continue
    seen.add(filename)
    out.push(filename)
  }
  return out
}

/**
 * tts3 edge 速度目录：不传或 -10 省略；其它为两位小数（-50 → -50.00，0 → 0.00）
 * @param {string|number|null|undefined} speedRatio
 */
export function ttsOssSpeedSegment (speedRatio) {
  if (speedRatio === '' || speedRatio == null) return ''
  const n = Number(speedRatio)
  if (!Number.isFinite(n) || n === -10) return ''
  return n.toFixed(2)
}

export function buildTtsOssObjectKey (voiceLabel, filename, speedRatio) {
  const folder = String(voiceLabel || '').trim() || 'Sonia'
  const file = String(filename || '').trim()
  if (!file) return ''
  const speedSeg = ttsOssSpeedSegment(speedRatio)
  if (speedSeg) return `sound/${folder}/${speedSeg}/${file}`
  return `sound/${folder}/${file}`
}

export function buildTtsOssPublicUrl (objectKey) {
  const key = String(objectKey || '').replace(/^\/+/, '')
  if (!key) return ''
  const withoutSound = key.replace(/^sound\//, '')
  return `${TTS_SOUND_OSS_BASE}/${encodeObjectKeyPath(withoutSound)}`
}

/** 同步拼出候选 URL（未预检） */
export function resolveTtsOssUrlCandidates (en, voiceType, speedRatio) {
  const folder = ttsVoiceFolderName(voiceType)
  return ttsMp3FilenameCandidates(en).map((filename) =>
    buildTtsOssPublicUrl(buildTtsOssObjectKey(folder, filename, speedRatio))
  )
}

function cacheKeyFor (en, voiceType, speedRatio) {
  const speedSeg = ttsOssSpeedSegment(speedRatio) || '-10'
  return `${ttsVoiceFolderName(voiceType)}|${speedSeg}|${String(en || '').trim().toLowerCase()}`
}

/** Map 插入序：读时挪到末尾，超上限删最旧 */
function hitCacheGet (key) {
  if (!hitCache.has(key)) return undefined
  const url = hitCache.get(key)
  hitCache.delete(key)
  hitCache.set(key, url)
  return url
}

function hitCacheSet (key, url) {
  if (hitCache.has(key)) hitCache.delete(key)
  hitCache.set(key, url)
  while (hitCache.size > HIT_CACHE_MAX) {
    const oldest = hitCache.keys().next().value
    hitCache.delete(oldest)
  }
}

function pruneMissCache (now = Date.now()) {
  for (const [k, until] of missCache) {
    if (until <= now) missCache.delete(k)
  }
  while (missCache.size > MISS_CACHE_MAX) {
    const oldest = missCache.keys().next().value
    missCache.delete(oldest)
  }
}

function missCacheGetUntil (key) {
  pruneMissCache()
  const until = missCache.get(key)
  if (until == null) return 0
  if (until <= Date.now()) {
    missCache.delete(key)
    return 0
  }
  return until
}

function missCacheSet (key, until) {
  if (missCache.has(key)) missCache.delete(key)
  missCache.set(key, until)
  pruneMissCache()
}

/** 是否为批量预生成 OSS 公网 URL（与 buildTtsOssPublicUrl 一致） */
export function isTtsOssPublicUrl (url) {
  const u = String(url || '').trim()
  if (!u) return false
  return u === TTS_SOUND_OSS_BASE || u.startsWith(`${TTS_SOUND_OSS_BASE}/`)
}

/**
 * OSS 预检命中但播放失败时调用：清会话 hitCache，避免同词反复走坏链
 * @param {string} en
 * @param {string} voiceType
 * @param {string|number} [speedRatio] 出站 speed_ratio
 */
export function invalidateTtsOssHit (en, voiceType, speedRatio) {
  const text = String(en || '').trim()
  if (!text) return
  hitCache.delete(cacheKeyFor(text, voiceType, speedRatio))
}

function ensureSharedProbeAudio () {
  if (typeof Audio === 'undefined') return null
  if (!sharedProbeAudio) {
    sharedProbeAudio = new Audio()
    sharedProbeAudio.preload = 'metadata'
  }
  return sharedProbeAudio
}

/** 对齐 ttsMixin._releaseTtsAudioElement：卸监听并释放解码缓冲 */
function releaseProbeAudioElement (audio) {
  if (!audio) return
  try {
    audio.oncanplay = null
    audio.onloadeddata = null
    audio.onerror = null
  } catch (_) { /* ignore */ }
  try {
    audio.pause()
  } catch (_) { /* ignore */ }
  try {
    audio.removeAttribute('src')
    audio.src = ''
  } catch (_) { /* ignore */ }
  try {
    audio.load()
  } catch (_) { /* ignore */ }
}

function probeTtsOssUrlOnce (url) {
  return new Promise((resolve) => {
    const src = String(url || '').trim()
    if (!src) {
      resolve(false)
      return
    }
    const audio = ensureSharedProbeAudio()
    if (!audio) {
      resolve(false)
      return
    }

    let settled = false
    const finish = (ok) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      releaseProbeAudioElement(audio)
      resolve(ok)
    }
    const timer = setTimeout(() => finish(false), PROBE_TIMEOUT_MS)

    releaseProbeAudioElement(audio)
    audio.preload = 'metadata'
    audio.oncanplay = () => finish(true)
    audio.onloadeddata = () => finish(true)
    audio.onerror = () => finish(false)
    audio.src = src
  })
}

/**
 * 用 Audio 预检（避免 OSS HEAD 的 CORS 问题）。
 * 全局排队 + 复用同一 Audio，结束时 pause/卸 src/load。
 */
export function probeTtsOssUrl (url) {
  const run = () => probeTtsOssUrlOnce(url)
  const next = probeChain.then(run, run)
  probeChain = next.then(() => {}, () => {})
  return next
}

/**
 * 预取：命中返回公网 URL，否则 ''
 * @param {string} en
 * @param {string} voiceType
 * @param {string|number} [speedRatio] 出站 speed_ratio；-10 / 空则无速度目录
 */
export async function resolveTtsOssUrl (en, voiceType, speedRatio) {
  const text = String(en || '').trim()
  if (!text || !enToFilename(text)) return ''

  const key = cacheKeyFor(text, voiceType, speedRatio)
  const cachedHit = hitCacheGet(key)
  if (cachedHit !== undefined) return cachedHit || ''

  const missUntil = missCacheGetUntil(key)
  if (missUntil > Date.now()) return ''

  const pending = inFlight.get(key)
  if (pending) return pending

  const task = (async () => {
    try {
      const candidates = resolveTtsOssUrlCandidates(text, voiceType, speedRatio)
      for (const url of candidates) {
        if (await probeTtsOssUrl(url)) {
          hitCacheSet(key, url)
          missCache.delete(key)
          return url
        }
      }
      missCacheSet(key, Date.now() + MISS_TTL_MS)
      return ''
    } finally {
      inFlight.delete(key)
    }
  })()

  inFlight.set(key, task)
  return task
}
